<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  onMounted,
} from 'vue'
import wait from '@/utilities/wait'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'RadioButtonDefault',

  props: {
    /**
     * 初期選択状態が bind される value
     */
    modelValue: {
      type: [String, Number, Boolean],
      default: undefined,
    },

    /**
     * フォームの name 属性
     */
    name: {
      type: String,
      default: '',
    },

    /**
     * 選択肢の項目情報
     */
    items: {
      type: Array as PropType<SelectItem[]>,
      required: true,
    },

    /**
     * 何列表示か
     */
    cols: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
      default: 3,
    },

    /**
     * disabled 属性にわたす非活性フラグ
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * エラー発生状態かどうか
     * ※ エラー状態は外から受け取る
     */
    isError: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'click:item'],

  setup(props, { emit }) {
    const localValue: Ref<SingleSelect | undefined> = ref()

    watch(() => props.modelValue, (newValue) => {
      if (localValue.value !== newValue) localValue.value = newValue
    })

    onMounted(() => {
      localValue.value = props.modelValue
    })

    /**
     * ラジオアイテムの選択変更関係なくクリックイベントを取得したい場合のイベント用メソッド
     */
    const onClickItem = async () => {
      /**
       * changed のイベント発火（update:modelValue の発火）を待機してからイベント通知する
       * nextTick だと、値の更新前にイベントが発火してしまうため wait 関数で待機する
       */
      await wait(0.1)
      emit('click:item', localValue.value)
    }

    return {
      // data
      localValue,

      // methods
      onClickItem,
    }
  }
})
</script>

<template>
  <div
    v-if="items"
    :class="[
      'o-list__cols',
      `is-col${cols}`,
    ]">
    <ul class="o-list__cols--items">
      <li
        v-for="item in items"
        :key="item.label"
        class="o-list__cols--item">
        <label
          class="label"
          :data-is-disabled="isDisabled"
          :data-is-error="isError">
          <input
            v-model="localValue"
            class="input"
            type="radio"
            :name="name ? name : undefined"
            :value="item.value"
            :disabled="isDisabled"
            :data-is-error="isError"
            @click="onClickItem"
            @change="$emit('update:modelValue', localValue)">{{ item.label }}
        </label>
      </li>
    </ul>
  <!-- /.o-list__cols --></div>
</template>

<style lang="sass" scoped>
.label
  // normalize のチェックボックスを基準としているので、具体的な変数の値は normalize（≒ mixins.sass の checkboxStyle）を参照
  $icon-size: 1em
  $margin-size: 0.3em
  display: inline-block
  padding-left: ($icon-size + $margin-size)

  .input[type=radio]
    margin-left: -#{$icon-size + $margin-size}
</style>
